import React, { useMemo } from 'react';
import classNames from 'classnames';
import { z } from 'zod';
import { merge } from 'lodash';
import type { SchemaType } from '$util/schema';
import type { ContentComponent } from '$cms/ContentComponent';
import { CmsImageSchema, CmsShortTextSchema } from '$cms/types';
import { HeadingLevels } from '@/components/atoms/HeadingLevels';
import { Grid, Row, Col } from '@/components/atoms/Grid';
import { Img } from '@/components/atoms/Img';

import styles from './index.module.scss';

const defaultConfiguration = {
  backgroundColor: '',
  textColor: '',
};

const LogosSchema = {
  translatedEntities: z.object({
    heading: CmsShortTextSchema.optional(),
    logo: z.record(CmsImageSchema),
  }),
  configuration: z
    .object({
      backgroundColor: z.string().optional(),
      textColor: z.string().optional(),
      lazyLoadImages: z.boolean().optional(),
      preloadImages: z.boolean().optional(),
    })
    .default(defaultConfiguration),
};

export type LogosTranslatedEntities = SchemaType<(typeof LogosSchema)['translatedEntities']>;
export type LogosConfiguration = SchemaType<(typeof LogosSchema)['configuration']>;

/**
 * A content component that always displays logos and optional short text.<br />
 * https://www.notion.so/fixter/Logos-a007505b98034e76bc821c9445f56521
 */
export const Logos: ContentComponent<LogosTranslatedEntities, LogosConfiguration> = function Logos({
  translatedEntities,
  configuration,
  className,
  ...props
}): JSX.Element {
  const { logo, heading } = translatedEntities;
  const { backgroundColor, textColor } = merge(defaultConfiguration, configuration);

  const logos = useMemo(() => Object.values(logo), [logo]);

  return (
    <section
      className={classNames(Logos.displayName, styles.component, className)}
      style={{
        // @ts-ignore Pass prop to styles
        '--background-color': backgroundColor || 'transparent',
        '--text-color': textColor || undefined,
      }}
      {...props}
    >
      <Grid className={styles.grid}>
        <Row className={styles.row}>
          {heading?.value && (
            <Col className={classNames(styles.col, styles.heading)} medium={3}>
              <HeadingLevels styleLevel={2} semanticLevel={1}>
                {heading.value}
              </HeadingLevels>
              {/* <Heading className={styles.heading}>{heading?.value}</Heading> */}
            </Col>
          )}
          <Col className={classNames(styles.col, styles.logos)} medium={9}>
            {logos.map(({ url, width, height, key, alt }) => (
              <div key={key} style={{ width }} className={styles.logoItem}>
                <Img key={key} src={url} alt={alt} width={width} height={height} layout="responsive" />
              </div>
            ))}
          </Col>
        </Row>
      </Grid>
    </section>
  );
};

Logos.displayName = 'Logos';
Logos.schema = LogosSchema;
